import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import useWideStage from '../../application/motion/useWideStage';
import { formatCompactPrice, formatPrice, formatSignedPercent, formatStampedMonth } from '../../domain/cars/carMoney';
import { formatSpecNumber } from '../../domain/cars/carProperties';
import { decadeTicks, investmentRating, valueHistory, valueTicks } from '../../domain/cars/carValue';
import { goldBenchmark, goldEquivalent } from '../../domain/cars/goldBenchmark';
import {
    HEADROOM,
    LABEL,
    PLOT_CLIP,
    projector,
    seriesPath,
    valueFrame,
} from './valueChartGeometry';

/**
 * What a car has been worth since it was new, drawn, and what that makes it.
 *
 * The same boxed plot the acceleration band uses, deliberately: these are two
 * graphs on one card and a reader should not have to learn a second chart to
 * read the second one. What differs is the honesty problem underneath, and every
 * difference in the markup below comes from it.
 *
 * The acceleration trace is measurements with interpolation between them. This
 * is measurements at the two ENDS with estimates in between — a list price the
 * marque published, an average of recorded sales read last month, and a
 * reconstruction of the thirty-nine years between them, because nobody publishes
 * what a 1993 Testarossa was worth in 1998. So:
 *
 * - **The two sourced points are solid marks, the estimates are hollow ones**,
 *   and the line joining them is BROKEN. Dashed is already this card's word for
 *   a value nothing measured — the acceleration band's ceiling is dashed for the
 *   same reason — so the convention is borrowed rather than invented.
 * - **The caption says which is which**, in words, under the chart. A visual
 *   convention that is never stated is a convention only its author knows.
 * - **The rating is computed from the two sourced ends alone.** It is the one
 *   thing on this band a reader might repeat to somebody, and grading a car on
 *   estimates is how an estimate becomes a verdict two conversations later.
 *
 * Behind both is the GOLD REFERENCE: the same money, put into gold in the same
 * year and held. It answers the "compared to what" the badge's inflation figure
 * answers only in the abstract, and it is drawn quietly — thin, dim and behind
 * everything — because this band is about the car and gold is the ruler beside
 * it. It is drawn SOLID against the car's broken line, which is the same
 * convention doing its job in the other direction: an annual gold average is a
 * published figure and the years between a car's anchors are not.
 *
 * The plot is scaled to the CAR, never to the reference, so the reference is
 * clipped and leaves the frame for a car gold beat by more than the axis can
 * show. That is the right way round: rescaling to fit gold would flatten the
 * Supra's own line — the shape this band exists to draw — into a smear along the
 * bottom. The caption prints the gold figure in words for exactly that case, so
 * the comparison is never only visual.
 *
 * The baseline is ZERO and not the cheapest year. Money has a true zero, and a
 * value axis floated off one turns the Corvette's ordinary decline into a cliff
 * — which is the single easiest lie to tell with this exact chart.
 *
 * NO HOVER LAYER, for the reason the acceleration band gives: this card is the
 * deck's swipe surface, and a chart that captured pointer events to show a
 * tooltip would take the gesture with it (SPEC-004 D-8). Every point is in the
 * table below.
 *
 * The SVG is `aria-hidden` and the same figures are a real `<table>`, with a
 * third column saying whether each row is sourced or estimated — because that
 * distinction is the point of the band, and a reader who cannot see the broken
 * line has to get it some other way.
 */

export default function CarValueChart({ car }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const history = valueHistory(car);
    const rating = investmentRating(car);

    // Nothing at all for a car the catalogue will not price. That is the
    // Yellowbird: twenty-nine cars, no trailing window of sales to average, and
    // `carPrices.js` refuses to put a hammer price under a label that says "what
    // one costs now" on thirteen other cards. A chart drawn to a figure the
    // catalogue would not print is that refusal undone in pictures.
    if (!history || !rating) {
        return null;
    }

    // The wide frame is chosen HERE rather than in CSS, because a `viewBox` is
    // an attribute and no media query can swap one drawing box for another. The
    // acceleration plate directly above this one is drawn on the same frame at
    // the same breakpoint, which is what keeps the pair looking like a pair.
    const frame = valueFrame(useWideStage());
    const { view, plot, plotWidth, plotHeight } = frame;
    const project = projector(history, frame);
    // The top of the DRAWN axis rather than of the data. The reference is
    // clipped to the plot, so "ran off the chart" has to mean past the headroom
    // as well — a line that stops a pixel inside the frame has not left it.
    const ceiling = history.ceiling * HEADROOM;
    const { currency } = history;
    const tick = (amount) => formatCompactPrice({ amount, currency }, { languageCode });
    const year = (value) => formatSpecNumber(value, { languageCode, grouped: false });
    const marketMonth = formatStampedMonth(car.price.market.asOf, { languageCode });
    // Both from the same call in effect, and both allowed to be null: a launch
    // year the benchmark series does not reach draws no reference rather than a
    // line from an assumed price.
    const holding = { from: history.from, to: history.to, amount: history.points[0].amount };
    const gold = goldBenchmark(holding);
    const goldToday = goldEquivalent(holding);

    return (
        <figure
            className="value-band"
            // NAMED, and the name is short on purpose. A `<figure>` takes its
            // accessible name from its `<figcaption>` when nothing else gives it
            // one — and this caption is three sentences and ninety words, so a
            // screen reader announces the whole of it on entering the figure and
            // then reads it again as content on the way out. `aria-label` wins
            // over the caption in the naming order, which leaves the caption
            // doing the one job it should: being read once, in place, as prose.
            aria-label={t('cars.value.figureLabel')}
        >
            <p className="value-band__verdict">
                <span className="value-band__verdict-label">{t('cars.value.ratingLabel')}</span>
                <span className={`value-badge value-badge--${rating.rating}`}>
                    {t(`cars.value.rating.${rating.rating}`)}
                </span>
            </p>

            <p className="value-band__axis-label value-band__axis-label--value" aria-hidden="true">
                {t('cars.value.valueAxis', { currency })}
            </p>

            <svg
                className="value-band__plot"
                viewBox={`0 0 ${view.width} ${view.height}`}
                aria-hidden="true"
                focusable="false"
            >
                <defs>
                    <clipPath id={PLOT_CLIP}>
                        <rect x={plot.left} y={plot.top} width={plotWidth} height={plotHeight} />
                    </clipPath>
                </defs>

                {/* The grid first, so every mark is drawn over it. */}
                <g className="value-band__grid">
                    {valueTicks(history.ceiling).map((amount) => (
                        <line
                            key={`value-${amount}`}
                            x1={plot.left}
                            x2={view.width - plot.right}
                            y1={project.y(amount)}
                            y2={project.y(amount)}
                        />
                    ))}
                    {decadeTicks(history.from, history.to).map((value) => (
                        <line
                            key={`year-${value}`}
                            x1={project.x(value)}
                            x2={project.x(value)}
                            y1={plot.top}
                            y2={plot.top + plotHeight}
                        />
                    ))}
                </g>

                {/* The two axes a reader measures against, solid where the grid
                    is dotted. The corner is the launch year at zero dollars, and
                    it has to look like an origin. */}
                <path
                    className="value-band__axis"
                    d={`M${plot.left} ${plot.top} L${plot.left} ${plot.top + plotHeight} L${view.width - plot.right} ${plot.top + plotHeight}`}
                />

                <g className="value-band__ticks">
                    {valueTicks(history.ceiling).map((amount) => (
                        <text key={`value-label-${amount}`} x={plot.left - 4} y={project.y(amount) + 3} textAnchor="end">
                            {tick(amount)}
                        </text>
                    ))}
                    {decadeTicks(history.from, history.to).map((value) => (
                        <text key={`year-label-${value}`} x={project.x(value)} y={view.height - 2} textAnchor="middle">
                            {year(value)}
                        </text>
                    ))}
                </g>

                {/* The ruler, under the thing it measures. Clipped, so a car
                    gold ran away from keeps its own axis and the reference
                    simply leaves the frame — the caption says where it went. */}
                {gold ? (
                    <path
                        className="value-band__gold"
                        clipPath={`url(#${PLOT_CLIP})`}
                        d={seriesPath(gold, project)}
                    />
                ) : null}

                <path className="value-band__series" d={seriesPath(history.points, project)} />

                {/* The two sourced figures, on the marks they belong to — and
                    ONLY those two. This is the one place where this band and the
                    acceleration band's rule genuinely diverge: there, every mark
                    is a measurement and every mark gets its number. Here the
                    middle of the series is this repository's reconstruction of
                    years nobody published, and a dollar figure printed beside a
                    hollow mark is exactly how an estimate stops reading as one.
                    The broken line says the middle is estimated; putting precise
                    money on it would say the opposite, louder.

                    The launch mark sits on the value axis and the market mark at
                    the far edge, so each label is anchored AWAY from its own end
                    of the frame — outward is the only direction with room. */}
                {history.points.filter((point) => point.sourced).map((point, index) => {
                    const atLaunch = index === 0;
                    const x = project.x(point.year) + (atLaunch ? LABEL.offset : -LABEL.offset);
                    const y = project.y(point.amount) - LABEL.offset;

                    return (
                        <g className="value-band__figure" key={`figure-${point.year}`}>
                            <text
                                className="value-band__figure-amount"
                                x={x}
                                y={y - LABEL.height}
                                textAnchor={atLaunch ? 'start' : 'end'}
                            >
                                {tick(point.amount)}
                            </text>
                            <text
                                className="value-band__figure-year"
                                x={x}
                                y={y}
                                textAnchor={atLaunch ? 'start' : 'end'}
                            >
                                {year(point.year)}
                            </text>
                        </g>
                    );
                })}

                {/* Two kinds of mark, and the difference is the whole band: a
                    solid one is a figure with a source, a hollow one is this
                    repository's estimate of a year nobody published. */}
                {history.points.map((point) => (
                    <circle
                        className={point.sourced ? 'value-band__point' : 'value-band__point value-band__point--estimated'}
                        key={point.year}
                        cx={project.x(point.year)}
                        cy={project.y(point.amount)}
                        r={point.sourced ? 3.5 : 2.5}
                    />
                ))}
            </svg>

            {/* A legend, because there are two lines now and telling them apart
                from the caption alone would mean reading a paragraph to find out
                which one is the car. `aria-hidden` with the rest of the drawing:
                the table below names its rows, and a swatch describes a picture
                nobody reading by ear is being shown.

                No label under the year axis, unlike the acceleration band's
                "Time (sec)". That one needs it because `4, 8, 12` could be
                anything; `1990, 2000, 2010` could not, and a third caption in
                this strip would read as a third series. */}
            <p className="value-band__legend" aria-hidden="true">
                <span className="value-band__key value-band__key--car">{t('cars.value.legend.car')}</span>
                {gold ? (
                    <span className="value-band__key value-band__key--gold">{t('cars.value.legend.gold')}</span>
                ) : null}
            </p>

            {/* The same figures, readable by ear — and the basis column, because
                a broken line is not a thing anyone can hear. */}
            <table className="visually-hidden">
                <caption>{t('cars.value.tableCaption')}</caption>
                <thead>
                    <tr>
                        <th scope="col">{t('cars.value.yearColumn')}</th>
                        <th scope="col">{t('cars.value.valueColumn')}</th>
                        <th scope="col">{t('cars.value.basisColumn')}</th>
                    </tr>
                </thead>
                <tbody>
                    {history.points.map((point) => (
                        <tr key={`row-${point.year}`}>
                            <td>{year(point.year)}</td>
                            <td>{formatPrice({ amount: point.amount, currency }, { languageCode })}</td>
                            <td>{t(point.sourced ? 'cars.value.basis.sourced' : 'cars.value.basis.estimated')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Two sentences and they do different jobs. The first is what the
                badge above MEANS, in figures, so a reader can disagree with the
                grade rather than only receive it. The second is what the chart is
                made of, which is the sentence that keeps the estimates from
                being read as measurements. */}
            <figcaption className="value-band__note">
                <span className="value-band__rate">
                    {t('cars.value.realRate', {
                        rate: formatSignedPercent(rating.real, { languageCode }),
                        years: year(rating.years),
                        multiple: formatSpecNumber(rating.multiple, { languageCode, fractionDigits: 1 }),
                        fromYear: year(rating.fromYear),
                    })}
                </span>
                {/* Printed rather than left to the line, and this is the one
                    sentence on the band that has to survive the drawing being
                    wrong: the plot is scaled to the car, so for the cars gold
                    beat by a wide margin the reference is off the top of the
                    frame and this is the only place the figure appears.

                    Which is also why the longer of the two keys is used ONLY
                    then. Explaining that a line left the chart is what a reader
                    needs when a line left the chart; on the ten cards where it
                    did not, it is a sentence about nothing. */}
                {goldToday === null ? null : (
                    <span className="value-band__gold-note">
                        {t(goldToday > ceiling ? 'cars.value.goldNoteOffChart' : 'cars.value.goldNote', {
                            stake: formatPrice({ amount: history.points[0].amount, currency }, { languageCode }),
                            fromYear: year(history.from),
                            amount: formatPrice({ amount: Math.round(goldToday), currency }, { languageCode }),
                        })}
                    </span>
                )}
                <span className="value-band__basis">
                    {t('cars.value.estimateNote', {
                        fromYear: year(history.from),
                        asOf: marketMonth ?? year(history.to),
                    })}
                </span>
            </figcaption>
        </figure>
    );
}
